import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchEvents } from '../api/api';
import { Calendar, ArrowRight, MapPin, Filter, Globe, Sparkles } from 'lucide-react';

const HomeScreen = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const data = await fetchEvents();
        setEvents(data || []);
      } catch (error) {
        console.error("Failed to load events");
      } finally {
        setLoading(false);
      }
    };
    loadEvents();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-cream text-secondary text-center">
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin mb-4"></div>
          <p className="font-black text-xs tracking-[0.3em] uppercase opacity-60">Preparing Divine Experience...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-cream min-h-screen font-outfit text-secondary selection:bg-primary/20 selection:text-secondary">
      {/* 1. Header Removed entirely as requested */}

      {/* 2. Hero Section - Updated with Updated Leeway Logo & Poster Content */}
      <section className="relative pt-12 pb-24 px-6 overflow-hidden">
        {/* Background Decorative Gradient Blurs */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -z-10 translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute top-1/2 left-0 w-64 h-64 bg-secondary/5 rounded-full blur-3xl -z-10 -translate-x-1/2"></div>

        <div className="max-w-7xl mx-auto relative z-10 flex flex-col items-center">

          {/* Centered Updated Leeway Logo (Based on the shared updated logo image) */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-16"
          >
            <div className="flex items-center gap-6 group cursor-default">
              {/* Crescent of dots representation of the updated logo */}
              {/* <div className="relative w-16 h-24 hidden sm:block">
                {[
                  { color: '#A3E635', top: '0%', left: '40%' },
                  { color: '#22C55E', top: '15%', left: '15%' },
                  { color: '#06B6D4', top: '40%', left: '0%' },
                  { color: '#3B82F6', top: '65%', left: '15%' },
                  { color: '#A855F7', top: '85%', left: '40%' },
                  { color: '#EC4899', top: '95%', left: '70%' },
                ].map((dot, i) => (
                  <div
                    key={i}
                    className="absolute w-3 h-3 rounded-full shadow-sm"
                    style={{ backgroundColor: dot.color, top: dot.top, left: dot.left }}
                  ></div>
                ))}
              </div> */}
              <div className="flex flex-col">

                {/* <h2 className="text-6xl md:text-7xl font-black tracking-tighter text-slate-800 leading-none">Leeway</h2> */}

                <div className="flex flex-col items-center gap-3 mt-4">
                  {/* Leeway Logo - centered, medium responsive size, transparent bg effect */}
                  <img
                    src="https://eventbooking.apanel.leewaysofttech.com/BOOK_YOUR_SHOW_API/upload/leeway_logo_new.png"
                    alt="Leeway Logo"
                    // Increased height to h-16 md:h-20 so it's not too small
                    // mix-blend-multiply removes the white background if your page bg is light
                    className="h-20 md:h-20 w-auto object-contain mix-blend-multiply"
                  />

                  {/* POWERED BY text - now strictly below the logo */}
                  <span className="text-[10px] font-black uppercase tracking-[0.4em] text-primary">
                    Powered By
                  </span>
                </div>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center w-full">
            {/* Impactful Short Content from the Poster */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center lg:text-left space-y-10 order-2 lg:order-1"
            >
              <div>
                <p className="text-secondary/40 font-black uppercase tracking-[0.5em] text-[10px] mb-4">Rotaract Bhavnagar (Youth)</p>
                <h1 className="text-5xl md:text-7xl font-playfair font-black text-secondary leading-[1.1] tracking-tight">
                  A PARTNERSHIP <br />
                  <span className="text-primary italic">BUILT ON TRUST</span>
                </h1>
                <div className="h-2 w-24 bg-primary mt-8 rounded-full mx-auto lg:mx-0"></div>
              </div>

              <div className="space-y-8 text-xl text-secondary/70 font-medium max-w-xl mx-auto lg:mx-0 leading-relaxed">
                <p>
                  Last year, they stood beside us as our <br />
                  <span className="text-secondary font-black border-b-2 border-primary/20 px-1">CO-SPONSOR</span> for <span className="text-primary font-bold">Shyam Shabad</span>.
                </p>

                <p>
                  This year, they return as our <br />
                  <span className="text-secondary font-black border-b-2 border-primary/20 px-1">TITLE PARTNER</span> for <span className="text-primary font-bold">Shiv Shabad</span>.
                </p>

                <p className="text-base text-secondary/40 italic pt-4 border-l-2 border-primary/10 pl-6">
                  Thank you, Leeway, for believing in our vision and becoming an integral part of this divine journey.
                </p>
              </div>

              <div className="pt-6 flex flex-col sm:flex-row items-center gap-6 justify-center lg:justify-start">
                <button
                  onClick={() => document.getElementById('events-section')?.scrollIntoView({ behavior: 'smooth' })}
                  className="w-full sm:w-auto px-10 py-5 bg-secondary text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-2xl shadow-secondary/20 hover:bg-primary transition-all active:scale-95"
                >
                  Book Shiv Shabad
                </button>
                <a
                  href="https://www.leewaysoftech.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-primary hover:text-secondary transition-colors"
                >
                  <Globe size={14} />
                  www.leewaysoftech.com
                </a>
              </div>
            </motion.div>

            {/* Poster Image Placement */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="order-1 lg:order-2"
            >
              <div className="rounded-[4rem] overflow-hidden shadow-[0_60px_100px_-20px_rgba(62,39,35,0.2)] border-[14px] border-white aspect-[4/5] max-w-lg mx-auto relative group">
                <img
                  src="https://leewaysoftech.com/static/media/partnership-poster.jpg"
                  alt="Shiv Shabad Divine Partnership"
                  className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                  onError={(e) => {
                    e.target.src = "https://eventbooking.apanel.leewaysofttech.com/BOOK_YOUR_SHOW_API/upload/shiv_shabad_logo.jpeg";
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-secondary/40 via-transparent to-transparent opacity-60 pointer-events-none" />
              </div>
              {/* Decorative background blurs */}
              <div className="absolute -bottom-10 -right-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3. Search Bar Removed entirely as requested */}

      {/* 4. Portion after Search Bar (Featured Events) - Kept as is */}
      <main id="events-section" className="max-w-7xl mx-auto px-6 mt-12 pb-32">
        <div className="flex flex-col md:flex-row items-end justify-between mb-16 gap-8">
          <div className="text-center md:text-left">
            <span className="text-primary font-black uppercase tracking-[0.4em] text-[10px]">Upcoming Divine Experiences</span>
            <h2 className="text-5xl font-playfair font-black text-secondary mt-3">Featured Performances</h2>
            <div className="h-1.5 w-24 bg-primary mt-6 rounded-full mx-auto md:mx-0"></div>
          </div>
          {/*<button className="flex items-center gap-3 px-8 py-4 bg-white border border-primary/10 rounded-2xl text-[10px] font-black uppercase tracking-widest text-secondary hover:bg-primary hover:text-white transition-all shadow-sm">
            <Filter size={18} className="text-primary" />
            Filter
          </button>*/}
        </div>

        <AnimatePresence mode='popLayout'>
          {events.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-40 bg-white/40 rounded-[4rem] border border-dashed border-primary/20"
            >
              <h3 className="text-2xl font-playfair font-black text-secondary/30 italic">No divine shows discovered yet.</h3>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-12">
              {events.map((event, index) => (
                <motion.div
                  key={event.id}
                  layout
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => navigate(`/event/${event.id}`, { state: { event } })}
                  className="group bg-white rounded-[3rem] overflow-hidden border border-primary/5 hover:shadow-[0_60px_100px_-20px_rgba(62,39,35,0.1)] transition-all duration-700 cursor-pointer"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={event.image_url}
                      alt={event.title}
                      className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                    />
                    <div className="absolute top-8 right-8 bg-white/95 backdrop-blur-sm px-5 py-2 rounded-2xl text-[10px] font-black text-primary uppercase tracking-widest shadow-xl border border-primary/10">
                      {event.category || 'Divine'}
                    </div>
                  </div>

                  <div className="p-10">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 rounded-2xl bg-primary/10 flex items-center justify-center text-primary">
                        <Calendar size={20} />
                      </div>
                      <span className="text-[10px] font-black text-secondary/40 uppercase tracking-[0.2em]">{event.date || 'Aug 02, 2025'}</span>
                    </div>

                    <h3 className="text-3xl font-playfair font-black text-secondary mb-4 group-hover:text-primary transition-colors leading-tight">
                      {event.title}
                    </h3>

                    <div className="flex items-center text-secondary/60 text-sm mb-10">
                      <MapPin size={18} className="mr-3 text-primary" />
                      <span className="font-bold">{event.location}</span>
                    </div>

                    <div className="flex items-center justify-between pt-8 border-t border-primary/10">
                      <div>
                        <p className="text-[10px] text-secondary/40 font-black uppercase tracking-[0.2em]">Starting From Just</p>
                        <p className="text-2xl font-black text-secondary italic">₹200 <span className="text-xs font-normal not-italic opacity-40"></span></p>
                      </div>
                      <div className="w-16 h-16 bg-primary/5 rounded-[1.5rem] flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-all duration-500 shadow-sm">
                        <ArrowRight size={28} />
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </AnimatePresence>
      </main>

      {/* 5. Footer Part and Stay Connected box removed entirely as requested */}
    </div>
  );
};

export default HomeScreen;
